import { useCallback, useSyncExternalStore } from 'react';

export interface NotificationSound { volume: number; muted: boolean }
const changed = 'skrepka-notification-sound-change';
const volatile = new Map<string, string>();
const keyFor = (userId?: number) => `skrepka.notifications.sound.${userId || 'guest'}`;
function snapshot(key: string) {
  if(volatile.has(key)) return volatile.get(key)!;
  try { return localStorage.getItem(key); } catch { return volatile.get(key) || null; }
}
function parse(raw: string | null): NotificationSound {
  try {
    const value: unknown = JSON.parse(raw || 'null');
    if(value && typeof value === 'object' && 'volume' in value && 'muted' in value && typeof value.volume === 'number' && Number.isFinite(value.volume) && value.volume > 0 && value.volume <= 1 && typeof value.muted === 'boolean') return {volume:value.volume,muted:value.muted};
  } catch { /* Use the existing full-volume default for missing/invalid settings. */ }
  return {volume:1,muted:false};
}
export function readNotificationSound(userId?: number) { return parse(snapshot(keyFor(userId))); }
export function useNotificationSound(userId?: number) {
  const key=keyFor(userId);
  const subscribe=useCallback((notify:()=>void)=>{
    const storage=(event:StorageEvent)=>{if(event.key===key || event.key===null){volatile.delete(key);notify();}};
    window.addEventListener('storage',storage);window.addEventListener(changed,notify);
    return ()=>{window.removeEventListener('storage',storage);window.removeEventListener(changed,notify);};
  },[key]);
  const getSnapshot=useCallback(()=>snapshot(key),[key]);
  const settings=parse(useSyncExternalStore(subscribe,getSnapshot,()=>null));
  const update=useCallback((next:NotificationSound)=>{
    const raw=JSON.stringify(next);
    try { localStorage.setItem(key,raw);volatile.delete(key); } catch { volatile.set(key,raw); }
    window.dispatchEvent(new Event(changed));
  },[key]);
  const setVolume=useCallback((volume:number)=>{
    if(!Number.isFinite(volume))return;
    const value=Math.max(0,Math.min(1,volume)), current=parse(snapshot(key));
    update({volume:value || current.volume,muted:value===0});
  },[key,update]);
  const toggle=useCallback(()=>{const current=parse(snapshot(key));update({...current,muted:!current.muted});},[key,update]);
  return {...settings,setVolume,toggle};
}
